import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {useSession} from '../../firebase/UserProvider';
import Timestamp from '../Timestamp';
import useYDocStore from '../../stores/useYDocStore';
import {
    buildThreadId,
    deleteComment,
    editComment,
    getCommentsForThread,
    observeThread,
} from '../prosemirror-editor/yMaps/commentsYMap';
import CommentMessageContent from './editor/CommentMessageContent';
import MessageComposer from '../MessageComposer';

/**
 * ListOfCommentsYjs
 *
 * Yjs-backed comment list.
 *
 * Props (passed as the `props` object):
 *   - elementName  {string}   e.g. "report-fragment"
 *   - elementId    {string}   e.g. "abc123"
 *   - fullHeight   {boolean}  flex-grow + scroll-to-bottom when true
 */
const ListOfCommentsYjs = ({props}) => {
    const {fullHeight, elementName, elementId} = props || {};
    const {user} = useSession();

    const ydoc = useYDocStore((s) => s.ydoc);
    const yComments = useYDocStore((s) => s.yComments);

    const [comments, setComments] = useState([]);
    const [editingMessageId, setEditingMessageId] = useState(null);
    const bottomRef = useRef(null);

    // ------------------------------------------------------------------
    // Subscribe to the Yjs thread
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!elementName || !elementId || !yComments) {
            setComments([]);
            return;
        }

        const threadId = buildThreadId(elementName, elementId);
        setComments(getCommentsForThread(yComments, threadId));

        const unsubscribe = observeThread(yComments, threadId, () => {
            setComments(getCommentsForThread(yComments, threadId));
        });

        return () => unsubscribe();
    }, [elementName, elementId, yComments]);

    // ------------------------------------------------------------------
    // Scroll to bottom when new messages arrive (fullHeight mode only)
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!fullHeight || !comments.length) return;
        bottomRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'});
    }, [comments, fullHeight]);

    // ------------------------------------------------------------------
    // Handlers
    // ------------------------------------------------------------------
    const handleDelete = (messageId) => {
        if (!ydoc || !yComments || !elementName || !elementId) return;
        const threadId = buildThreadId(elementName, elementId);
        deleteComment(ydoc, yComments, threadId, messageId);
    };

    const handleEditStart = (messageId) => setEditingMessageId(messageId);
    const handleEditDone = useCallback(() => setEditingMessageId(null), []);

    const buildEditSubmitHandler = useCallback((comment) => (json) => {
        if (!ydoc || !yComments || !elementName || !elementId) return;
        try {
            const threadId = buildThreadId(elementName, elementId);
            editComment(ydoc, yComments, threadId, comment.messageId, json, 'prosemirror');
            handleEditDone();
        } catch (e) {
            console.error('Failed to edit comment:', e);
        }
    }, [ydoc, yComments, elementName, elementId, handleEditDone]);

    // Build initial ProseMirror content from a comment for edit mode
    const buildInitialContent = (comment) => {
        if (comment.contentType === 'prosemirror' && comment.content && typeof comment.content === 'object') {
            return comment.content;
        }
        const text = typeof comment.content === 'string' ? comment.content : '';
        return {
            type: 'doc',
            content: [{type: 'paragraph', content: text ? [{type: 'text', text}] : []}],
        };
    };

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------
    return (
        <Stack flex={fullHeight ? 1 : 'none'} height={fullHeight ? '100%' : 'auto'} overflow={'auto'}>
            {comments.map((comment) => {
                const isOwn = user && comment.userId === user.uid;
                const isEditing = editingMessageId === comment.messageId;

                return (
                    <Stack
                        key={comment.messageId}
                        spacing={0.5}
                        py={1}
                        px={2}
                        borderBottom={'1px solid #eee'}
                    >
                        {/* Header row: name / date + action buttons */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant={'body2'} color={'text.secondary'}>
                                {comment.userDisplayName},{' '}
                                <Timestamp component="span" value={comment.publishedDatetime}/>
                                {comment.editedAt && (
                                    <Box
                                        component="span"
                                        sx={{ml: 0.5, fontStyle: 'italic', fontSize: '0.75rem', color: 'text.disabled'}}
                                    >
                                        (edited)
                                    </Box>
                                )}
                            </Typography>

                            {isOwn && !isEditing && (
                                <Stack direction="row" spacing={0}>
                                    <Tooltip title="Edit">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditStart(comment.messageId)}
                                            aria-label="edit comment"
                                        >
                                            <EditIcon sx={{fontSize: 14}}/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(comment.messageId)}
                                            aria-label="delete comment"
                                        >
                                            <DeleteOutlineIcon sx={{fontSize: 14}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            )}
                        </Stack>

                        {/* Content row — inline editor when editing, plain text otherwise */}
                        {isEditing ? (
                            <MessageComposer
                                onSubmitRich={buildEditSubmitHandler(comment)}
                                onCancel={handleEditDone}
                                initialContent={buildInitialContent(comment)}
                                placeholder="Edit message…"
                                submitLabel="Save"
                                autoFocus
                            />
                        ) : (
                            <CommentMessageContent content={comment.content} contentType={comment.contentType}/>
                        )}
                    </Stack>
                );
            })}
            <div ref={bottomRef}/>
        </Stack>
    );
};

export default ListOfCommentsYjs;
